/**
 * JotRx Encounter Action Hook
 *
 * Shared business logic for JotRx encounter CTAs.
 * Handles setup state, patient connection checks, DoseSpot SSO,
 * connect modal state, and patient demographics fetching.
 */

import { type MouseEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccessToken } from '@features/user';
import APIService from '@services/APIService';
import { useEventTracking } from '@services/statsig';
import { getPatientPrimaryContactPhone } from '@utils/patientUtils';
import { useSnackbar } from 'notistack';

import {
  useGetPatientPrescribingReadinessQuery,
  useLazyGetPatientSsoQuery,
} from '../store';

import { useJotRxSetupState } from './useJotRxSetupState';
import { useProxyOnBehalfOf } from './useProxyOnBehalfOf';

interface PatientAddressLike {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

interface PatientDemographics {
  first_name?: string;
  last_name?: string;
  dob?: string;
  sex?: string;
  gender?: string;
  mobile_phone?: string;
  phone?: string;
  primary_address?: PatientAddressLike | null;
  // Legacy flat address fields, kept as fallback
  address?: string;
  address_2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface PatientConnectData {
  firstName?: string;
  lastName?: string;
  dob?: string;
  gender?: string;
  phone?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface UseJotRxEncounterActionOptions {
  patientId?: string | null;
  patientData?: PatientConnectData;
  source?: 'encounter_cta' | 'patient_detail' | 'medications_table';
}

function getRouterBasename() {
  const basename = import.meta.env.VITE_ROUTER_BASENAME?.trim();

  if (!basename || basename === '/') {
    return '';
  }

  return `/${basename.replace(/^\/+|\/+$/g, '')}`;
}

function buildPrescribeLauncherUrl(
  patientId: string,
  source: string,
  onBehalfOfProviderId?: string,
) {
  const params = new URLSearchParams({ source });
  if (onBehalfOfProviderId) {
    params.set('on_behalf_of_provider_id', onBehalfOfProviderId);
  }
  return (
    `${getRouterBasename()}/jotrx/prescribe-launch/` +
    `${encodeURIComponent(patientId)}?${params.toString()}`
  );
}

export function useJotRxEncounterAction({
  patientId,
  patientData,
  source = 'encounter_cta',
}: UseJotRxEncounterActionOptions) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { jotrx } = useEventTracking();
  const { setupState, isJotRxEnabled, isJotRxPurchasable, isLoading, cta, isFullySetup } = useJotRxSetupState();
  const {
    data: prescribingReadiness,
    isLoading: isPatientStatusLoading,
    refetch: refetchReadiness,
  } = useGetPatientPrescribingReadinessQuery(
    { patientId: patientId || '' },
    { skip: !patientId || !isFullySetup },
  );
  const { getAccessToken } = useAccessToken();
  const [getPatientSso] = useLazyGetPatientSsoQuery();
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [patientDemographics, setPatientDemographics] = useState<PatientDemographics | null>(null);
  const [pendingOnBehalfOfProviderId, setPendingOnBehalfOfProviderId] = useState<string | undefined>();

  const { isProxy, prescribers } = useProxyOnBehalfOf();
  const [proxyMenuAnchor, setProxyMenuAnchor] = useState<null | HTMLElement>(null);
  const isPatientConnected = prescribingReadiness?.connected ?? false;

  // Proxies have no personal JotRx entitlement; the org being connected to
  // DoseSpot is what gates them (enforced by setupState + backend).
  const isVisible =
    (isJotRxEnabled || isJotRxPurchasable || isProxy) && !isLoading;

  // Fetch patient demographics for connect modal pre-fill
  useEffect(() => {
    if (!patientId || patientData) {
      setPatientDemographics(null);
      return;
    }
    let cancelled = false;
    const fetchPatientData = async () => {
      try {
        const accessToken = await getAccessToken();
        const response = await APIService.makeAPIGetRequest({
          requestString: `/patient/get?patient_id=${patientId}&origin=JotRxConnect`,
          accessToken,
        });
        if (response.ok && !cancelled) {
          const data = (await response.value) as { patient?: PatientDemographics } | undefined;
          setPatientDemographics(data?.patient || null);
        }
      } catch {
        // Non-critical — modal still works without pre-fill
      }
    };
    void fetchPatientData();
    return () => { cancelled = true; };
  }, [patientId, patientData, getAccessToken]);

  const openPatientSso = useCallback(async (onBehalfOfProviderId?: string) => {
    if (!patientId) return;
    const ssoWindow = window.open('about:blank', '_blank');
    if (!ssoWindow) {
      enqueueSnackbar('Popup blocked — please allow popups for this site.', { variant: 'warning' });
      return;
    }
    try {
      const result = await getPatientSso({
        patientId,
        ...(onBehalfOfProviderId ? { onBehalfOfProviderId } : {}),
      }).unwrap();
      if (result.sso_url) {
        ssoWindow.opener = null;
        ssoWindow.location.href = result.sso_url;
        jotrx.prescribeOpened({ source, patient_id: patientId });
      } else {
        ssoWindow.close();
      }
    } catch {
      ssoWindow.close();
      enqueueSnackbar('Failed to open DoseSpot. Please try again.', { variant: 'error' });
    }
  }, [patientId, getPatientSso, enqueueSnackbar, jotrx, source]);

  const openPrescribeLauncher = useCallback((onBehalfOfProviderId?: string) => {
    if (!patientId) return false;
    const launcherWindow = window.open(
      buildPrescribeLauncherUrl(patientId, source, onBehalfOfProviderId),
      '_blank',
      'noopener,noreferrer',
    );
    if (!launcherWindow) {
      enqueueSnackbar('Popup blocked — please allow popups for this site.', { variant: 'warning' });
      return false;
    }
    return true;
  }, [patientId, source, enqueueSnackbar]);

  const launchPrescribe = useCallback(async (onBehalfOfProviderId?: string) => {
    if (!patientId) return;
    if (!prescribingReadiness) {
      enqueueSnackbar('Checking e-prescription readiness. Please try again in a moment.', { variant: 'info' });
      return;
    }

    if (prescribingReadiness.connected) {
      await openPatientSso(onBehalfOfProviderId);
      return;
    }

    if (prescribingReadiness.connectable_from_chart) {
      openPrescribeLauncher(onBehalfOfProviderId);
      return;
    }

    setPendingOnBehalfOfProviderId(onBehalfOfProviderId);
    setShowConnectModal(true);
  }, [
    patientId,
    prescribingReadiness,
    enqueueSnackbar,
    openPatientSso,
    openPrescribeLauncher,
  ]);

  const handleClick = useCallback(async (e?: MouseEvent<HTMLElement>) => {
    jotrx.setupStarted({ source, setup_state: setupState });

    if (setupState === 'fully_setup' && patientId) {
      if (isPatientStatusLoading || !prescribingReadiness) {
        enqueueSnackbar('Checking e-prescription readiness. Please try again in a moment.', { variant: 'info' });
        return;
      }
      if (isProxy) {
        // GDN-1119: proxy must act on behalf of a connected prescriber.
        if (prescribers.length === 0) {
          enqueueSnackbar(
            'A prescriber in your organization must finish DoseSpot setup ' +
              'before you can prepare prescriptions on their behalf.',
            { variant: 'warning' },
          );
          return;
        }
        if (prescribers.length === 1) {
          await launchPrescribe(prescribers[0].userId);
        } else if (e) {
          setProxyMenuAnchor(e.currentTarget);
        }
        return;
      }
      await launchPrescribe();
      return;
    }

    navigate(cta.route);
  }, [
    jotrx,
    source,
    setupState,
    patientId,
    isPatientStatusLoading,
    prescribingReadiness,
    isProxy,
    prescribers,
    enqueueSnackbar,
    launchPrescribe,
    cta.route,
    navigate,
  ]);

  const handleConnectSuccess = useCallback(async () => {
    refetchReadiness();
    const onBehalfOfProviderId =
      pendingOnBehalfOfProviderId ||
      (isProxy && prescribers.length === 1 ? prescribers[0].userId : undefined);
    setPendingOnBehalfOfProviderId(undefined);

    if (isProxy && !onBehalfOfProviderId) {
      enqueueSnackbar(
        'A prescriber in your organization must finish DoseSpot setup ' +
          'before you can prepare prescriptions on their behalf.',
        { variant: 'warning' },
      );
      return;
    }

    await openPatientSso(onBehalfOfProviderId);
  }, [
    refetchReadiness,
    pendingOnBehalfOfProviderId,
    isProxy,
    prescribers,
    enqueueSnackbar,
    openPatientSso,
  ]);

  const selectProxyPrescriber = useCallback(
    (userId: string) => {
      setProxyMenuAnchor(null);
      void launchPrescribe(userId);
    },
    [launchPrescribe],
  );

  const fetchedConnectModalPatientData = patientDemographics
    ? {
        firstName: patientDemographics.first_name,
        lastName: patientDemographics.last_name,
        dob: patientDemographics.dob || undefined,
        gender: patientDemographics.sex || patientDemographics.gender,
        phone: getPatientPrimaryContactPhone(patientDemographics),
        address: patientDemographics.primary_address?.line1 || patientDemographics.address,
        address2: patientDemographics.primary_address?.line2 || patientDemographics.address_2,
        city: patientDemographics.primary_address?.city || patientDemographics.city,
        state: patientDemographics.primary_address?.state || patientDemographics.state,
        zipCode: patientDemographics.primary_address?.zip || patientDemographics.zip,
      }
    : undefined;
  const connectModalPatientData = patientData || fetchedConnectModalPatientData;

  return {
    setupState,
    isVisible,
    isFullySetup,
    isJotRxPurchasable,
    isPatientConnected,
    isPatientStatusLoading,
    isLoading,
    cta,
    handleClick,
    showConnectModal,
    setShowConnectModal,
    handleConnectSuccess,
    connectModalPatientData,
    // GDN-1119 proxy → prescriber picker (only when >1 connected prescriber)
    proxyPrescribers: prescribers,
    proxyMenuAnchor,
    closeProxyMenu: () => setProxyMenuAnchor(null),
    selectProxyPrescriber,
  };
}
